import "dayjs/locale/ja";
import { useSelector } from "react-redux";
import { RootState } from "../../store/types";
import { Button, Card, Col, DatePicker, FloatButton, Form, Input, InputNumber, List, Radio, Row, Select, Statistic, Table, Tabs, Tag } from "antd";
import { ArrowRightOutlined, EditOutlined, LoadingOutlined, PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import HrnModal from "../../components/HrnModal";
import validateMessages, { requiredRule } from "../../utils/validate";
import { useEffect, useState } from "react";
import { useBookServiceMutation, useLazyGetServicesQuery, useUpdateNextServicesMutation, useUpdateServiceMutation } from "../../services/service";
import dayjs from "dayjs";
import { useGetServiceCentersQuery } from "../../services/serviceCenter";
import { useTranslation } from "react-i18next";
import hrnUtils from "../../utils/hrnUtils";
import { DateFormat, SerialStatus, SerialStatusTagColor } from "../../shared/constants";
import HrnAvatar from "../../components/HrnAvatar";
import TextArea from "antd/es/input/TextArea";
import { useLazyGetServiceCenterServiceTypesQuery } from "../../services/serviceType";
import useScreenSize from "../../hooks/useScreenSize";
import tableUtils from "../../utils/tableUtils";
import dateUtils from "../../utils/dateUtils";
import { useGetServiceManQuery } from "../../services/serviceMan";

const SerialBoard = () => {
    const { loggedInUser } = useSelector((state: RootState) => state.global);
    const { t } = useTranslation();
    const { isMobile } = useScreenSize();
    const [serialForm] = Form.useForm();
    const [open, setOpen] = useState(false);
    const [serviceCenter, setServiceCenter] = useState<any | null>(null);
    const [serviceDate, setServiceDate] = useState<any | null>(dayjs());
    const [queueServices, setQueueServices] = useState<any[]>([]);
    const [servedServices, setServedServices] = useState<any[]>([]);
    const [updateForm] = Form.useForm();
    const [selectedService, setSelectedService] = useState<any | null>(null);
    const [openUpdateForm, setOpenUpdateForm] = useState(false);
    const [serviceCenters, setServiceCenters] = useState<any[]>([]);
    const [serviceTypes, setServiceTypes] = useState<any[]>([]);
    const [status, setStatus] = useState<string | null>(null);
    const [servingSerialNos, setServingSerialNos] = useState<number[]>([]);
    const [serviceDateToday, setServiceDateToday] = useState<boolean>(true);
    const [updateSerial, updateResult] = useUpdateServiceMutation();
    const [getServiceTypes, getServiceTypesResult] = useLazyGetServiceCenterServiceTypesQuery();
    const [getServices, getServicesResult] = useLazyGetServicesQuery();
    const [bookForSerial, bookResult] = useBookServiceMutation();
    const [updateNextServices, updateNextServicesResult] = useUpdateNextServicesMutation();

    const {
        data: serviceCentersResonse,
        isFetching: isSCFetching,
        isSuccess: isSCSuccess,
    } = useGetServiceCentersQuery({
        companyId: loggedInUser?.currentCompany?.id
    });

    const {
        data: serviceMan,
        isFetching: isSMFetching,
        isSuccess: isSMSuccess,
    } = useGetServiceManQuery({
        companyId: loggedInUser?.currentCompany?.id,
        userId: loggedInUser?.id
    });

    useEffect(() => {
        if (getServiceTypesResult.isSuccess) {
            setServiceTypes(getServiceTypesResult.data || []);
        }
    }, [getServiceTypesResult.isFetching]);

    const onServiceCenterChanged = async (value: string) => {
        if (serviceCenters?.length) {
            const serviceCenter = serviceCenters?.find((x: any) => x.id === value);
            setServiceCenter({ ...serviceCenter, id: value });
        }
        await getServices({ serviceCenterId: value, serviceDate: serviceDate?.format("YYYY-MM-DD") }).unwrap();
        await getServiceTypes({ serviceCenterId: value, defaultIfNotSet: true }).unwrap();
    }

    const onServiceDateChanged = async (value: any) => {
        setServiceDate(value);
        setServiceDateToday(value?.format("YYYYMMDD") === dayjs().format("YYYYMMDD"))
        await getServices({ serviceCenterId: serviceCenter?.id, serviceDate: value?.format("YYYY-MM-DD") }).unwrap();
    };

    const onRefreshed = async () => {
        await getServices({ serviceCenterId: serviceCenter?.id, serviceDate: serviceDate?.format("YYYY-MM-DD") }).unwrap();
    };

    useEffect(() => {
        if (isSCSuccess) {
            const centers = (serviceCentersResonse || [])
                .filter((x: any) => ((isSMSuccess ? serviceMan : {}).serviceCenterIds || []).includes(x.id));

            setServiceCenters(centers);
            const id = centers?.[0]?.id;
            setServiceCenter(centers?.[0]);
            if (id)
                onServiceCenterChanged(id);
        }
    }, [isSCFetching, isSMFetching]);


    useEffect(() => {
        if (getServicesResult.isSuccess) {
            const srvs = getServicesResult.data || [];
            const servings = srvs.filter((x: any) => x.status === SerialStatus.SERVING)?.map((x: any) => x.serialNo);
            const queue = srvs.filter((x: any) => x.status !== SerialStatus.SERVED);
            const served = srvs.filter((x: any) => x.status === SerialStatus.SERVED).sort((x: any, y: any) => new Date(y.statusTime).getTime() - new Date(x.statusTime).getTime());
            setServingSerialNos(servings || []);
            setQueueServices(queue);
            setServedServices(served);
        }
    }, [getServicesResult.isFetching]);

    const openSerialDialog = (service: any = null) => {
        if (service !== null) {
            serialForm.setFieldsValue({
                serviceCenterName: serviceCenters.find((x: any) => x.id === service?.serviceCenterId)?.name,
                ...service
            });
        } else {
            serialForm.setFieldValue("serviceCenterName", serviceCenter?.name);
        }
        setSelectedService(service);
        setOpen(true);
    };

    const closeSerialDialog = () => {
        setOpen(false);
        setSelectedService(null);
        serialForm.resetFields();
    };

    const createSerial = async () => {
        try {
            await serialForm.validateFields();
            const formFields = serialForm.getFieldsValue();
            if (!formFields.id) {
                await bookForSerial({
                    serviceCenterId: serviceCenter?.id,
                    body: {
                        ...formFields,
                        serviceDate: formFields.serviceDate.format("YYYY-MM-DD"),
                        forSelf: false,
                        isAdmin: true
                    }
                }).unwrap();
            } else if (selectedService != null) {
                await updateSerial({
                    serviceCenterId: selectedService.serviceCenterId,
                    id: formFields.id,
                    body: {
                        serviceTypeId: formFields.serviceTypeId,
                        forSelf: false,
                        name: formFields.name,
                        contactNo: formFields.contactNo,
                        date: formFields.serviceDate?.format("YYYY-MM-DD")
                    }
                }).unwrap();
            }
            setOpen(false);
            serialForm.resetFields();
        } catch (errorInfo) {
            return;
        }
    };

    const openUpdateDialog = (service: any) => {
        if (service == null) return;
        updateForm.setFieldsValue({
            ...service,
            charge: service.status === SerialStatus.SERVED ? service.charge : service.serviceType?.price
        });
        setOpenUpdateForm(true);

        setSelectedService(service);
        setStatus(service.status);
    };

    const closeUpdateDialog = () => {
        setOpenUpdateForm(false);
        setSelectedService(null);
        setStatus(null);
        updateForm.resetFields();
    };

    const updateSerialStatus = async () => {
        try {
            await updateForm.validateFields();
            const formFields = updateForm.getFieldsValue();
            await updateSerial({
                serviceCenterId: selectedService?.serviceCenterId,
                id: selectedService?.id,
                body: {
                    isPresent: formFields.status !== SerialStatus.BOOKED && formFields.status !== SerialStatus.CANCELLED && formFields.status !== SerialStatus.ABSENT,
                    status: formFields.status,
                    charge: formFields.status === SerialStatus.SERVED ? formFields.charge : null,
                    comment: formFields.comment
                }
            }).unwrap();
            setOpenUpdateForm(false);
            updateForm.resetFields();
        } catch (errorInfo) {
            return;
        }
    };

    const nextSerial = async () => {
        try {
            const servedService = await updateNextServices({
                serviceCenterId: serviceCenter?.id,
                body: {
                    date: dayjs().format("YYYY-MM-DD")
                }
            }).unwrap();
            if (servedService?.status === SerialStatus.SERVED)
                openUpdateDialog(servedService);
        } catch (errorInfo) {
            return;
        }
    };

    const renderSerialTemplate = (record: any, index: number) => (
        <List.Item key={record.id} className="!p-0">
            <Row className="w-full" >
                <Col className="w-[30px]">
                    <h2 className="mt-1 text-primary">{record.serialNo}.</h2>
                </Col>
                <Col style={{ width: "calc(100% - 90px)" }}  >
                    <List.Item.Meta
                        avatar={<HrnAvatar user={record.user} size={"large"} />}
                        title={<>
                            {record.forSelf && <span className="text-primary" >{record.user?.name}</span>}
                            {!record.forSelf && <span className="text-primary" >{record.name}</span>}
                            {(record.isAdmin || record.isReserved) && serviceDateToday && record.status !== SerialStatus.SERVING && record.status !== SerialStatus.SERVED && <Button type="link" className="p-0 m-0 ml-[5px]" onClick={() => openSerialDialog(record)}><EditOutlined /></Button>}
                        </>
                        }
                        description={<div className="mt-[-10px]">
                            <small>{dayjs(record.createdTime + "Z").format("ddd hh:mm:ss A")}</small>
                            {/* {!record.forSelf && <small className="text-[#ccc] whitespace-nowrap ml-[3px] max-w-[100px] inline-block text-ellipsis overflow-hidden">(By {record.isAdmin ? "Admin" : record.user.name})</small>} */}
                            <div>{record.serviceType.name}</div>
                        </div>}
                    />
                </Col>
                <Col className="w-[60px]">
                    <Button disabled={!serviceDateToday} type="text" onClick={() => openUpdateDialog(record)} className="m-0 p-0" ><Tag className="m-0" color={SerialStatusTagColor[record.status as SerialStatus]}>{record.status}</Tag></Button>
                    <div style={{ fontSize: "0.5rem", whiteSpace: "nowrap" }}>
                        {dayjs(record.statusTime + "Z").format("ddd hh:mm:ss A")}
                    </div>
                    <div className="text-right" style={{ fontSize: "0.7rem" }}>{record.charge && `${record.charge} BDT`}</div>
                </Col>
            </Row>
        </List.Item>
    );

    const columns = (mode: "queque" | "served"): any[] => {
        const cols = [
            {
                key: "serialNo",
                header: "S/L#",
                className: "w-[50px] font-medium !text-right",
                fn: (value: any, record: any) => value
            },
            {
                key: "id",
                header: "Customer",
                className: "font-medium",
                fn: (value: any, record: any) => (<>
                    <div className="flex items-center gap-1 text-primary">
                        <HrnAvatar user={!record.isReserved && record.user} size={"small"} />
                        <span >{record.forSelf ? record.user.name : record.name}</span>
                        {(record.isAdmin || record.isReserved) && serviceDateToday && record.status !== SerialStatus.SERVING && record.status !== SerialStatus.SERVED && <Button type="link" className="p-0 m-0 ml-[5px]" onClick={() => openSerialDialog(record)}><EditOutlined /></Button>}
                    </div>
                    {/* {!record.forSelf && <div className="pl-[25px] mt-[-10px]">
                        <small className="text-[#ccc] whitespace-nowrap">(By {record.isAdmin ? "Admin" : record.user.name})</small>
                    </div>} */}
                </>)
            },
            {
                key: "contactNo",
                header: "Contact No.",
                className: "font-medium",
                fn: (value: any, record: any) => (<>{record.forSelf ? record.user.mobileNo : record.contactNo}</>)
            },
            {
                key: "createdTime",
                header: "S/L Time",
                className: "font-medium",
                fn: (value: any, record: any) => (<>{dateUtils.localTime(record.createdTime)?.format("ddd hh:mm:ss A")}</>)
            },
            {
                key: "serviceTypeId",
                header: "Service Type",
                className: "font-medium",
                fn: (value: any, record: any) => (<>{record.serviceType?.name}</>)
            },
            {
                key: "status",
                header: "Status",
                className: "font-medium",
                fn: (value: any, record: any) => (
                    <Button type="text" disabled={!serviceDateToday} onClick={() => openUpdateDialog(record)} className="m-0 p-0" ><Tag className="m-0" color={SerialStatusTagColor[record.status as SerialStatus]}>{record.status}</Tag></Button>
                )
            },
            {
                key: "statusTime",
                header: "Status Time",
                className: "font-medium",
                fn: (value: any, record: any) => (<>{dateUtils.localTime(record.statusTime)?.format("ddd hh:mm:ss A")}</>)
            }
        ];
        if (mode === "queque")
            return cols;
        else return [...cols, {
            key: "charge",
            header: "Amount",
            className: "font-medium !text-right",
            fn: (value: any, record: any) => (<>{record.charge} BDT</>)
        },]
    }

    return (<>
        <div className="flex justify-between items-center pb-2">
            {isMobile && serviceDateToday && servingSerialNos?.length > 0 && <FloatButton
                type="primary" icon={null}
                description={<h1>{servingSerialNos.join(",")}</h1>}
                tooltip={`Running: ${servingSerialNos.join(",")}`}
                style={{ insetBlockStart: 100, insetInlineEnd: 0, width: "64px", height: "64px" }}

            />}
            <Row className="w-full">
                <Col className={isMobile ? "w-full" : "w-[80%]"}>
                    <Card className="serial-board-date text-md xs:text-sm lg:text-lg">
                        <Statistic
                            title="Today"
                            value={dayjs().format("dddd, DD MMMM YYYY")}
                            valueStyle={{ color: "#3f8600", fontSize: "20px" }}
                        />
                    </Card>
                </Col>
                {!isMobile && <Col className="w-[20%]">
                    <Card className="serial-board-running text-md xs:text-sm lg:text-lg">
                        <Statistic
                            title="Running"
                            value={serviceDateToday ? servingSerialNos.join(",") || "-" : "-"}
                            valueStyle={{ color: "#3f8600", fontSize: "20px" }}
                        />
                    </Card>
                </Col>}
            </Row>
        </div>
        <div className="flex justify-between items-center gap-3 pb-1 w-full">
            {!isMobile && <div className="w-[120px]">Service Center: </div>}
            <Select
                style={{ width: isMobile ? "100%" : "calc(70% - 100px)" }}
                placeholder={t("Service Center")}
                options={(isSCSuccess ? serviceCenters : [])
                    .map((x: any) => ({ label: x.name, value: x.id }))}
                showSearch
                filterOption={hrnUtils.dropdownFilterOption}
                value={serviceCenter?.id}
                onChange={onServiceCenterChanged}
            />
            {!isMobile && <>
                <div className="w-[120px]">Service Date: </div>
                <DatePicker
                    style={{ width: isMobile ? "100%" : "calc(30% - 120px)" }}
                    className=""
                    allowClear={false}
                    format={DateFormat.USUAL}
                    onKeyDown={(e: any) => hrnUtils.handleDateFieldKeyDownEvent(e)}
                    defaultValue={serviceDate}
                    inputMode="none"
                    onChange={onServiceDateChanged}
                />
            </>}
        </div>
        {isMobile && <div className={"flex justify-between items-center gap-3 pb-1 w-full"}>
            <div className="w-[150px]">Service Date: </div>
            <DatePicker
                style={{ width: isMobile ? "100%" : "calc(100% - 150px)" }}
                className=""
                allowClear={false}
                format={DateFormat.USUAL}
                onKeyDown={(e: any) => hrnUtils.handleDateFieldKeyDownEvent(e)}
                defaultValue={serviceDate}
                inputMode="none"
                onChange={onServiceDateChanged}
            />
        </div>}
        <div className="flex justify-between items-center gap-3 pb-2">
            <Button type="primary" onClick={() => openSerialDialog()} disabled={!serviceDateToday}>
                <PlusOutlined />  New Serial
            </Button>
            <Button type="primary" disabled={queueServices.length === 0 || updateNextServicesResult.isLoading || !serviceDateToday} onClick={nextSerial} >
                Next <ArrowRightOutlined />
            </Button>
        </div>
        <div className="flex items-center justify-end w-full">
            <Button className="pr-0 pb-0 z-[100]"
                type="link"
                icon={<ReloadOutlined />}
                onClick={onRefreshed}
            >Refresh</Button>
        </div>
        <div className="tabs w-full mt-[-40px]">
            <Tabs className="mt-0 mb-0" defaultActiveKey="1" items={[
                {
                    key: "1",
                    className: "mt-0 mb-0",
                    label: queueServices.length === 0 ? "Queue" : `Queue(${queueServices.length})`,
                    children: <>{isMobile ? <List
                        loading={getServicesResult.isFetching || updateNextServicesResult.isLoading}
                        dataSource={queueServices}
                        renderItem={renderSerialTemplate}
                    /> :
                        <Table tableLayout="fixed"
                            rowKey="id"
                            columns={tableUtils.buildColumns(
                                getServicesResult.isSuccess ? queueServices : [],
                                columns("queque")
                            )}
                            dataSource={queueServices}
                            size="middle"
                            className="ant_table"
                            loading={{
                                spinning: (getServicesResult.isFetching || updateNextServicesResult.isLoading) as boolean,
                                indicator: <LoadingOutlined style={{ fontSize: 34 }} />,
                            }}
                            pagination={false}
                        />
                    }</>
                },
                {
                    key: "2",
                    className: "mt-0 mb-0",
                    label: servedServices.length === 0 ? "Served" : `Served(${servedServices.length})`,
                    children: <>{isMobile ? <List
                        loading={getServicesResult.isFetching || updateNextServicesResult.isLoading}
                        dataSource={servedServices}
                        renderItem={renderSerialTemplate}
                        footer={
                            servedServices.length > 0 &&
                            <div className="w-full flex font-bold">
                                <div className="floar-left w-[40%]">Total: &nbsp;&nbsp; {servedServices.length} Person(s)</div>
                                <div className="float-right w-[60%] text-right">{servedServices.reduce((sum, s) => sum + s?.charge || 0, 0)} BDT</div>
                            </div>}
                    /> :
                        <Table tableLayout="fixed"
                            rowKey="id"
                            columns={tableUtils.buildColumns(
                                getServicesResult.isSuccess ? servedServices : [],
                                columns("served")
                            )}
                            dataSource={servedServices}
                            size="middle"
                            className="ant_table"
                            loading={{
                                spinning: isSCFetching as boolean,
                                indicator: <LoadingOutlined style={{ fontSize: 34 }} />,
                            }}
                            pagination={false}
                            summary={() =>
                            (<>
                                {servedServices.length > 0 && <tr>
                                    <th colSpan={7} className="text-left">Total: &nbsp;&nbsp; {servedServices.length} Person(s)</th>
                                    <th className="text-right">{servedServices.reduce((sum, s) => sum + s?.charge || 0, 0)} BDT</th>
                                </tr>}
                            </>
                            )}
                        />
                    }</>
                },
            ]} />

        </div>
        <HrnModal
            open={openUpdateForm}
            title={`Serial# ${selectedService?.serialNo} - ${selectedService?.name}`}
            width={520}
            isLoading={updateResult.isLoading}
            submitButtonTitle={"Update"}
            onCancel={closeUpdateDialog}
            onSave={updateSerialStatus}
            afterClose={closeUpdateDialog}
        >
            <div className="mt-2">
                <Form
                    form={updateForm}
                    layout="vertical"
                    name="updateForm"
                    validateMessages={validateMessages}
                >
                    <Form.Item name="status" label={"Serial Status"} >
                        <Radio.Group
                            options={[SerialStatus.BOOKED,
                            SerialStatus.PRESENT,
                            SerialStatus.WAITING,
                            SerialStatus.SERVING,
                            SerialStatus.SERVED,
                            SerialStatus.CANCELLED,
                            SerialStatus.ABSENT].map((x) => ({ label: x, value: x }))}
                            optionType="button"
                            buttonStyle="solid"
                            size={isMobile ? "small" : "middle"}
                            onChange={(e) => setStatus(e.target.value)}
                        />
                    </Form.Item>
                    {status === SerialStatus.SERVED && <Form.Item name="charge" label={"Collected Amount (BDT)"} >
                        <InputNumber className="w-full" inputMode="decimal" min={0} placeholder="Amount(BDT)" suffix="BDT" />
                    </Form.Item>}
                    <Form.Item name="comment" label={"Comment"} >
                        <TextArea size="large" placeholder="Comment" />
                    </Form.Item>
                </Form>
            </div>
        </HrnModal>
        <HrnModal
            open={open}
            title={"New Serial"}
            width={520}
            isLoading={bookResult.isLoading}
            submitButtonTitle={"Ok"}
            onCancel={closeSerialDialog}
            onSave={createSerial}
            afterClose={closeSerialDialog}
        >
            <div className="mt-6">
                <Form
                    form={serialForm}
                    layout="vertical"
                    name="serialForm"
                    validateMessages={validateMessages}
                >
                    <Form.Item name={"id"} label={"Id"} hidden>
                        <Input size="large" placeholder="Id" disabled />
                    </Form.Item>
                    <Form.Item name={"serviceCenterName"} label={<>Service Center <span style={{ color: "red", marginLeft: "2px" }}>*</span></>} >
                        <Input size="large" placeholder="Name" disabled />
                    </Form.Item>
                    <Form.Item name="serviceTypeId" label={<>{t("Service Type")}<span style={{ color: "red", marginLeft: "2px" }}>*</span></>} rules={[requiredRule]}>
                        <Select placeholder={t("Service Type")} options={serviceTypes.map((x) => ({ label: x.name, value: x.id }))} />
                    </Form.Item>
                    <Form.Item name="serviceDate" label={<>{t("Date")}<span style={{ color: "red", marginLeft: "2px" }}>*</span></>} rules={[requiredRule]} initialValue={dayjs()}>
                        <DatePicker
                            className="w-full"
                            allowClear={false}
                            format={DateFormat.USUAL}
                            disabledDate={(date: any) => date < dayjs().add(-1, "days") || date > dayjs().add((serviceCenter?.daysOfAdvanceSerial || 0), "days")}
                            onKeyDown={(e: any) => hrnUtils.handleDateFieldKeyDownEvent(e)}
                        />
                    </Form.Item>
                    <Form.Item name="name" label={<>Name <span style={{ color: "red", marginLeft: "2px" }}>*</span></>} rules={[requiredRule]} >
                        <Input size="large" placeholder="Name" />
                    </Form.Item>
                    <Form.Item name="contactNo" label={<>{t("Contact No.")}<span style={{ color: "red", marginLeft: "2px" }}>*</span></>} rules={[requiredRule]} initialValue={"01"}>
                        <Input inputMode="tel" size="large" placeholder="Contact No." />
                    </Form.Item>
                </Form>
            </div>
        </HrnModal>
    </>
    );
};

export default SerialBoard;
